import { useMemo } from 'react';
import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { QueryKey, RegisterUserRequest, UserFilter, UserPublic } from 'api/model';
import { toast } from 'react-toastify';
import { errorHandler } from 'api/axios_instance';
import { registerUser } from 'api/auth/requests';
import { getUsersPage } from './requests';

const queryKey = QueryKey.Users;
const PAGE_SIZE = 25;

type Props = {
  sort?: string[];
  filters?: UserFilter[];
  enabled?: boolean;
};

const useUsers = ({ sort, filters, enabled = true }: Props = {}) => {
  const queryClient = useQueryClient();

  const { data, isLoading, isFetching, fetchNextPage, hasNextPage, isFetchingNextPage, error } = useInfiniteQuery({
    queryKey: [queryKey, { sort, filters }],
    queryFn: ({ pageParam }) => getUsersPage({ page: pageParam, pageSize: PAGE_SIZE, sort, filters }),
    getNextPageParam: (lastPage: UserPublic[], pages) => (lastPage.length === PAGE_SIZE ? pages.length + 1 : null),
    initialPageParam: 1,
    enabled,
  });

  const users = useMemo(() => data?.pages.flat() ?? [], [data]);

  const create = useMutation({
    mutationFn: (model: RegisterUserRequest) => registerUser(model),
    onSuccess: (user) => {
      void queryClient.invalidateQueries({ queryKey: [queryKey] });
      toast.success(`User ${user.username} successfully created`);
    },
    onError: errorHandler('Creating user failed'),
  });

  return { users, isLoading, isFetching, fetchNextPage, hasNextPage, isFetchingNextPage, error, create };
};

export { queryKey, PAGE_SIZE };
export default useUsers;
